import { createReducer } from '@reduxjs/toolkit';
import { createEmptyOptions, preloadedOptionsState } from './options.preloaded-state';
import {
  getAllSelectOptionsThunk,
  getCountrySelectOptionsThunk,
  getCitySelectOptionsThunk,
  addCityToDictionaryThunk,
  getEmployeesByRolesOptionsThunk,
  getUnitSelectOptionsThunk,
  getDivisionSelectOptionsThunk,
  getDepartmentSelectOptionsThunk,
} from './options.thunk';
import { localizedNameObject } from '@innowise-group/utilities';
import { clearOptionsAction } from './options.actions';
import { Options } from './options.types';

const mergeOptions = (currentOptions: Options | null, updatedOptions: Partial<Options>): Options => ({
  ...createEmptyOptions(),
  ...currentOptions,
  ...updatedOptions,
});

export const optionsReducer = createReducer(preloadedOptionsState, (builder) =>
  builder
    .addCase(getEmployeesByRolesOptionsThunk.pending, (state) => {
      state.employeesLoading = true;
    })
    .addCase(getEmployeesByRolesOptionsThunk.fulfilled, (state, { payload }) => {
      state.employeesLoading = false;
      state.options = mergeOptions(state.options, {
        statusAuthorsOptions: payload.map(({ id, ...data }) => {
          return {
            title: localizedNameObject(data).localizedFullName,
            value: id.toString(),
            miniPhotoUrl: data.photoRt?.id?.toString() || data.miniPhotoHrmUrl || null,
          };
        }),
      });
    })
    .addCase(getEmployeesByRolesOptionsThunk.rejected, (state) => {
      state.employeesLoading = false;
    })
    .addCase(getAllSelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getAllSelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, payload);
    })
    .addCase(getAllSelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(addCityToDictionaryThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(addCityToDictionaryThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, {
        citiesOptions: [...(state.options?.citiesOptions || []), payload],
      });
    })
    .addCase(addCityToDictionaryThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(getCountrySelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getCountrySelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, { ...payload.options, countriesOptions: payload.items });
    })
    .addCase(getCountrySelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(getCitySelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getCitySelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, {
        citiesOptions: payload.citiesOptions,
        locationOptions: payload.locationOptions,
      });
    })
    .addCase(getCitySelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(clearOptionsAction, (state) => {
      state.options = preloadedOptionsState.options;
    })
    .addCase(getUnitSelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getUnitSelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, {
        unitOptions: payload,
      });
    })
    .addCase(getUnitSelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(getDivisionSelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getDivisionSelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, {
        divisionOptions: payload,
      });
    })
    .addCase(getDivisionSelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    })
    .addCase(getDepartmentSelectOptionsThunk.pending, (state) => {
      state.loading = true;
    })
    .addCase(getDepartmentSelectOptionsThunk.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.options = mergeOptions(state.options, {
        departmentOptions: payload,
      });
    })
    .addCase(getDepartmentSelectOptionsThunk.rejected, (state) => {
      state.loading = false;
    }),
);
